import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import './StatsPlot.css';

const StatsPlot = ({ data, currentTime, dataKey, label, unit, color = '#667eea' }) => {
  // Filter data up to current time and prepare for chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Filter data up to current time
    const filtered = data.filter(d => d.timestamp <= currentTime);
    
    if (filtered.length === 0) {
      // If no data before current time, return initial point
      return [{ timestamp: currentTime, value: 0 }];
    }
    
    // Convert to chart format
    return filtered.map(d => ({
      timestamp: d.timestamp,
      value: d[dataKey],
      // Format time for display (relative to start)
      timeLabel: new Date(d.timestamp * 1000).toLocaleTimeString()
    }));
  }, [data, currentTime, dataKey]);

  // Calculate current value
  const currentValue = useMemo(() => {
    if (chartData.length === 0) return 0;
    return chartData[chartData.length - 1].value;
  }, [chartData]);

  // Format value with unit
  const formatValue = (value) => {
    if (value == null || typeof value !== 'number' || isNaN(value)) {
      return `0 ${unit}`;
    }
    if (dataKey === 'maxVelocitymps') {
      // Convert m/s to km/h
      return `${(value * 3.6).toFixed(1)} ${unit}`;
    }
    return `${value.toFixed(1)} ${unit}`;
  };

  // Format timestamp for tooltip
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString();
  };

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className="stats-plot-container">
      <div className="stats-plot-header">
        <span className="stats-plot-label">{label}</span>
        <span className="stats-plot-value">{formatValue(currentValue)}</span>
      </div>
      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={chartData} margin={{ top: 2, right: 5, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333333" />
          <XAxis 
            dataKey="timestamp" 
            tickFormatter={formatTimestamp}
            stroke="#cccccc"
            fontSize={10}
            tick={{ fill: '#cccccc' }}
          />
          <YAxis 
            stroke="#cccccc"
            fontSize={10}
            tick={{ fill: '#cccccc' }}
            width={35}
            tickFormatter={(value) => {
              if (value == null || typeof value !== 'number' || isNaN(value)) return '0';
              return dataKey === 'maxVelocitymps' ? (value * 3.6).toFixed(0) : value.toFixed(0);
            }}
          />
          <Tooltip 
            formatter={(value) => formatValue(value)}
            labelFormatter={formatTimestamp}
            contentStyle={{ backgroundColor: '#222222', border: '1px solid #444444', borderRadius: '4px', color: '#ffffff' }}
            labelStyle={{ color: '#ffffff' }}
          />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {currentTime && (
            <ReferenceLine 
              x={currentTime} 
              stroke="#ff6b6b" 
              strokeDasharray="3 3"
              label={{ value: "Now", position: "top", fill: "#ff6b6b" }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StatsPlot;

